import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FiActivity } from 'react-icons/fi';
import { IoWarningOutline } from 'react-icons/io5';
import { COLORS } from '../../constants';
import { useCardStyles } from '../../styles';
import MetricCard from './MetricCard';

// Estilos específicos de ParticipationCard
const useLocalStyles = makeStyles((theme) => ({
    percentageAvg: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        fontSize: '16px',
    },
    warningIcon: {
        color: COLORS.warning,
        fontSize: '20px',
    },
    warningValue: {
        color: COLORS.warning,
        fontWeight: 'bold',
        fontSize: '16px',
    },
}));

/**
 * Tarjeta de métricas de participación
 * Muestra total de mediciones, promedio de participación y usuarios con baja participación
 * @param {Object} props - Props del componente
 * @param {Object} props.data - Datos de participación
 * @param {number} props.data.totalMeasurements - Total de mediciones
 * @param {number} props.data.averageParticipation - Promedio de participación
 * @param {number} props.data.lowParticipationUsers - Usuarios con baja participación
 * @param {string} props.month - Mes actual
 * @param {number} props.year - Año actual
 * @returns {JSX.Element} Tarjeta de participación
 */
export default function ParticipationCard({ 
    data = {},
    month = '',
    year = '',
}) {
    const cardClasses = useCardStyles();
    const localClasses = useLocalStyles();

    const {
        totalMeasurements = 0,
        averageParticipation = 0,
        lowParticipationUsers = 0,
    } = data;

    const periodLabel = month && year ? `${month} ${year}` : 'Sin período';

    // Contenido de la métrica principal
    const mainMetric = (
        <>
            <Typography className={cardClasses.metricValue}>
                {totalMeasurements.toLocaleString()} Mediciones
            </Typography>
            <Typography className={cardClasses.metricLabel}>
                Total realizadas
            </Typography>
        </>
    );

    // Contenido de la fila de estadísticas
    const statsRow = (
        <>
            <Box className={localClasses.percentageAvg}>
                <FiActivity className={cardClasses.percentageIcon} />
                {averageParticipation}%
            </Box>
            <Typography className={cardClasses.metricLabel}>
                Promedio de participación
            </Typography>
        </>
    );

    // Contenido de la fila de advertencia
    const warningRow = (
        <>
            <IoWarningOutline className={localClasses.warningIcon} />
            <Typography className={localClasses.warningValue}>
                {lowParticipationUsers}
            </Typography>
            <Typography className={cardClasses.warningLabel}>
                Usuarios con baja participación
            </Typography>
        </>
    );

    return (
        <MetricCard
            title="Participación"
            subtitle={periodLabel}
            mainMetric={mainMetric}
            statsRow={statsRow}
            warningRow={warningRow}
        />
    );
}

ParticipationCard.propTypes = {
    data: PropTypes.shape({
        totalMeasurements: PropTypes.number.isRequired,
        averageParticipation: PropTypes.number.isRequired,
        lowParticipationUsers: PropTypes.number.isRequired,
    }).isRequired,
    month: PropTypes.string.isRequired,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
